// Partograma (051, Fase 4 Tanda 2a, ver PLAN.md "FASE 4 — spec completa" →
// "051 — Historia Clínica Materna Perinatal + Partograma" → Página 2) —
// gráfico de dilatación cervical × horas de registro con curva de alerta
// por percentil 10, y la tabla horaria (columnas a-p) de constantes
// materno-fetales que va debajo. Mismo criterio que denverII.ts/
// odontograma.ts: catálogo/geometría acá, dibujo vectorial con `pdf-lib`.
//
// La tabla "VALORES PARA LA CONSTRUCCIÓN DE LAS CURVAS DE ALERTA (en horas)
// p10" (Schwarcz/Díaz/Nieto, CLAP) NO estaba transcrita en forma numérica en
// PLAN.md (solo descrita cualitativamente) — se leyó directamente de la
// imagen de la página 179 del PDF fuente (footer
// MSP/DNEAIS/DNCSS/form.051/mayo/2016) para no inventar valores clínicos.
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { BORDE_SUTIL, GRIS_OSCURO, INK, MUTED } from './layout';

/** Combinaciones válidas — "todas" solo existe con membranas íntegras en la tabla impresa (no hay columna "todas + rotas"). */
export const PARIDAD_MEMBRANA_OPCIONES = [
  { paridad: 'todas', membranas: 'integras' },
  { paridad: 'multiparas', membranas: 'integras' },
  { paridad: 'multiparas', membranas: 'rotas' },
  { paridad: 'nuliparas', membranas: 'integras' },
  { paridad: 'nuliparas', membranas: 'rotas' },
] as const satisfies readonly { paridad: string; membranas: string }[];
export type ParidadMembranaPartograma = (typeof PARIDAD_MEMBRANA_OPCIONES)[number];
export type ParidadPartograma = ParidadMembranaPartograma['paridad'];
export type MembranasPartograma = ParidadMembranaPartograma['membranas'];

/**
 * Minutos p10 para avanzar de (cm-1) a cm, por columna paridad+membranas —
 * transcritos de la tabla impresa. La dilatación de partida (línea de base)
 * es siempre 4cm en el formulario original — el percentil 10 se construye
 * sumando estos minutos desde ahí.
 */
const MINUTOS_POR_CM: Readonly<Record<number, Readonly<Record<string, number>>>> = {
  5: { 'todas-integras': 130, 'multiparas-integras': 150, 'multiparas-rotas': 150, 'nuliparas-integras': 195, 'nuliparas-rotas': 150 },
  6: { 'todas-integras': 75, 'multiparas-integras': 85, 'multiparas-rotas': 60, 'nuliparas-integras': 90, 'nuliparas-rotas': 85 },
  7: { 'todas-integras': 60, 'multiparas-integras': 55, 'multiparas-rotas': 35, 'nuliparas-integras': 60, 'nuliparas-rotas': 65 },
  8: { 'todas-integras': 35, 'multiparas-integras': 40, 'multiparas-rotas': 25, 'nuliparas-integras': 40, 'nuliparas-rotas': 50 },
  9: { 'todas-integras': 25, 'multiparas-integras': 25, 'multiparas-rotas': 10, 'nuliparas-integras': 35, 'nuliparas-rotas': 35 },
  10: { 'todas-integras': 15, 'multiparas-integras': 15, 'multiparas-rotas': 5, 'nuliparas-integras': 30, 'nuliparas-rotas': 20 },
};
const DILATACION_BASE = 4;
const ESCALONES_CM = [5, 6, 7, 8, 9, 10] as const;

export interface PuntoPartograma {
  readonly hora: number;
  readonly dilatacion: number;
}

/** Curva de alerta p10 completa (7 puntos, de 4cm en `horaBase` hasta 10cm) — pura, testeable sin PDF. */
export function curvaAlertaPartograma(horaBase: number, paridad: ParidadPartograma, membranas: MembranasPartograma): readonly PuntoPartograma[] {
  const clave = `${paridad}-${membranas}`;
  const puntos: PuntoPartograma[] = [{ hora: horaBase, dilatacion: DILATACION_BASE }];
  let minutosAcumulados = 0;
  for (const cm of ESCALONES_CM) {
    minutosAcumulados += MINUTOS_POR_CM[cm][clave] ?? 0;
    puntos.push({ hora: horaBase + minutosAcumulados / 60, dilatacion: cm });
  }
  return puntos;
}

/** Planos de Hodge impresos en la escala derecha del papel; el valor De Lee es el que el papel imprime junto a cada plano (el escaneo muestra "4" arriba, se lee como -4 por la serie -4/-2/0/+4). */
export const PLANOS_HODGE_PARTOGRAMA = [
  { plano: 'I', deLee: '-4', fcfEquivalente: 190 },
  { plano: 'II', deLee: '-2', fcfEquivalente: 160 },
  { plano: 'III', deLee: '0', fcfEquivalente: 130 },
  { plano: 'IV', deLee: '+4', fcfEquivalente: 100 },
] as const;
export type PlanoHodgePartograma = (typeof PLANOS_HODGE_PARTOGRAMA)[number]['plano'];

/** Dips de la FCF según las referencias del papel: I (tipo I, desaceleración precoz), II (tipo II, tardía), V (variable) y M (meconio). */
export const TIPOS_DIP_PARTOGRAMA = ['I', 'II', 'V', 'M'] as const;
export type TipoDipPartograma = (typeof TIPOS_DIP_PARTOGRAMA)[number];

/** Posición materna (referencias del papel): LD lat. derecho, LI lat. izquierdo, D dorsal, SS semisentada, S sentada, PC parada o caminando. */
export const POSICIONES_MATERNAS_PARTOGRAMA = ['LD', 'LI', 'D', 'SS', 'S', 'PC'] as const;
export type PosicionMaternaPartograma = (typeof POSICIONES_MATERNAS_PARTOGRAMA)[number];

/** Localización e intensidad del dolor/contracción (referencias del papel). */
export const LOCALIZACIONES_DOLOR_PARTOGRAMA = ['SP', 'S', 'U'] as const;
export type LocalizacionDolorPartograma = (typeof LOCALIZACIONES_DOLOR_PARTOGRAMA)[number];
export const INTENSIDADES_DOLOR_PARTOGRAMA = ['+++', '++', '+', '-'] as const;
export type IntensidadDolorPartograma = (typeof INTENSIDADES_DOLOR_PARTOGRAMA)[number];

export interface PuntoFcfPartograma {
  readonly hora: number;
  readonly fcf: number;
}
export interface PuntoHodgePartograma {
  readonly hora: number;
  readonly plano: PlanoHodgePartograma;
  readonly variedad?: string;
}
export interface DipPartograma {
  readonly hora: number;
  readonly tipo: TipoDipPartograma;
}

export const COLUMNAS_HORARIAS_PARTOGRAMA = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p'] as const;
export type ColumnaHorariaPartograma = (typeof COLUMNAS_HORARIAS_PARTOGRAMA)[number];

/** Una columna (a-p) de la tabla horaria impresa bajo el gráfico — solo los campos con dato, igual criterio que `EntradaOdontogramaPdf`. */
export interface FilaHorariaPartogramaPdf {
  readonly columna: ColumnaHorariaPartograma;
  readonly horaReal?: string;
  readonly posicionMaterna?: string;
  readonly tensionArterial?: string;
  readonly pulsoMaterno?: string;
  readonly frecuenciaCardiacaFetal?: string;
  readonly duracionContracciones?: string;
  readonly frecuenciaContracciones?: string;
  /** Texto libre heredado (modelo viejo): se sigue leyendo/dibujando. */
  readonly dolor?: string;
  readonly dolorLocalizacion?: string;
  readonly dolorIntensidad?: string;
}

const CAMPOS_TABLA_HORARIA: readonly { clave: keyof Omit<FilaHorariaPartogramaPdf, 'columna'>; etiqueta: string }[] = [
  { clave: 'horaReal', etiqueta: 'Hora real' },
  { clave: 'posicionMaterna', etiqueta: 'Posición materna' },
  { clave: 'tensionArterial', etiqueta: 'Tensión arterial' },
  { clave: 'pulsoMaterno', etiqueta: 'Pulso materno' },
  { clave: 'frecuenciaCardiacaFetal', etiqueta: 'FCF' },
  { clave: 'duracionContracciones', etiqueta: 'Duración contracciones' },
  { clave: 'frecuenciaContracciones', etiqueta: 'Frec. contracciones' },
  { clave: 'dolor', etiqueta: 'Dolor/Localiz./Intens.' },
];

/** Wire format serializable de `EspecificacionPdf` (modo 'msp', campo `diagramaPartograma`) — solo 051. `filasHorarias` ya viene dispersa (solo columnas con algún dato). */
export interface DatosPartogramaPdf {
  readonly puntos: readonly PuntoPartograma[];
  readonly horaBaseLineaAlerta?: number;
  readonly paridad?: ParidadPartograma;
  readonly membranas?: MembranasPartograma;
  readonly filasHorarias: readonly FilaHorariaPartogramaPdf[];
  /** Todo lo de abajo es opcional: los PDF/datos anteriores no lo traen. */
  readonly fcf?: readonly PuntoFcfPartograma[];
  readonly hodge?: readonly PuntoHodgePartograma[];
  readonly dips?: readonly DipPartograma[];
  /** Hora de registro de la ruptura espontánea (REM) / artificial (RAM) de membranas. */
  readonly rem?: number;
  readonly ram?: number;
}

/** ¿Hay algo que dibujar? (dilatación, FCF, Hodge, dips, REM/RAM o tabla horaria). */
export function partogramaTieneDatos(d: DatosPartogramaPdf): boolean {
  return d.puntos.length > 0 || d.filasHorarias.length > 0 || (d.fcf?.length ?? 0) > 0 || (d.hodge?.length ?? 0) > 0 || (d.dips?.length ?? 0) > 0 || d.rem !== undefined || d.ram !== undefined;
}

/** Valor de la celda "Dolor/Localiz./Intens." con el texto viejo y los campos nuevos. */
export function textoDolorPartograma(f: FilaHorariaPartogramaPdf): string {
  return [f.dolor, f.dolorLocalizacion, f.dolorIntensidad].filter((v) => v && v.trim() !== '').join(' ');
}

const HORA_MAX = 14;
const DILATACION_MIN = 1;
const DILATACION_MAX = 10;
// La grilla del papel tiene una fila más arriba del 10: es el 200 de la escala FCF (100 en el 1 cm, +10 lpm por fila).
const FILA_MAX = 11;
const FCF_MIN = 100;
const MARGEN_DERECHO = 44;
const ALTO_GRAFICO = 150;
const ALTO_TITULO = 14;
const ALTO_EJE_X = 12;
const ALTO_LEYENDA = 12;
const ALTO_FILA_TABLA = 9;
const ALTO_TITULO_TABLA = 12;
const ANCHO_COLUMNA_ETIQUETA = 95;
const EJE_Y_W = 16;

// Mismos hex que ya usa `signosVitalesTimeline.ts` para pine/clay (paleta
// hce-* del sistema, no colores nuevos): dilatación real = pine, curva de
// alerta p10 = clay (punteada, como en el papel).
const PINE = rgb(0x1f / 255, 0x4d / 255, 0x47 / 255);
const CLAY = rgb(0xb5 / 255, 0x56 / 255, 0x3c / 255);

/** Espacio vertical a reservar con `cursor.dibujo(altoDibujoPartograma(datos), ...)` — 0 si no hay nada que dibujar. */
export function altoDibujoPartograma(datos: DatosPartogramaPdf): number {
  if (!partogramaTieneDatos(datos)) return 0;
  const altoTabla = datos.filasHorarias.length > 0 ? ALTO_TITULO_TABLA + CAMPOS_TABLA_HORARIA.length * ALTO_FILA_TABLA + 10 : 0;
  return ALTO_TITULO + ALTO_GRAFICO + ALTO_EJE_X + ALTO_LEYENDA * 2 + 6 + altoTabla;
}

function dibujarTablaHoraria(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, filas: readonly FilaHorariaPartogramaPdf[]): number {
  let y = origen.yTop;
  pagina.drawText('Registro horario (a-p)', { x: origen.x, y, size: 8.5, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO_TABLA;

  const anchoColumnaDato = (anchoDisponible - ANCHO_COLUMNA_ETIQUETA) / filas.length;

  // Encabezado con la letra de cada columna presente.
  for (const [i, fila] of filas.entries()) {
    const x = origen.x + ANCHO_COLUMNA_ETIQUETA + i * anchoColumnaDato;
    pagina.drawText(fila.columna, { x: x + anchoColumnaDato / 2 - 2, y, size: 7, font: fontRegular, color: MUTED });
  }
  y -= ALTO_FILA_TABLA;

  for (const campo of CAMPOS_TABLA_HORARIA) {
    pagina.drawText(campo.etiqueta, { x: origen.x, y, size: 6.5, font: fontRegular, color: GRIS_OSCURO });
    for (const [i, fila] of filas.entries()) {
      const valor = campo.clave === 'dolor' ? textoDolorPartograma(fila) : fila[campo.clave];
      if (!valor) continue;
      const x = origen.x + ANCHO_COLUMNA_ETIQUETA + i * anchoColumnaDato;
      pagina.drawText(String(valor), { x, y, size: 6.5, font: fontRegular, color: INK, maxWidth: anchoColumnaDato - 2 });
    }
    y -= ALTO_FILA_TABLA;
  }
  return origen.yTop - y;
}

/** Fila de la grilla (1 = 100 lpm / 1 cm, 11 = 200 lpm) que corresponde a una FCF — misma escala impresa en el eje derecho del papel. */
export function filaDeFcf(fcf: number): number {
  return (fcf - FCF_MIN) / 10 + DILATACION_MIN;
}

/** Dibuja el gráfico dilatación×horas + curva de alerta + FCF, planos de Hodge/De Lee, dips, REM/RAM y la tabla horaria sobre una `PDFPage` cruda, entregada por `CursorPdf.dibujo()`. Pura: no toca I/O, solo `pdf-lib`. */
export function dibujarPartograma(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, datos: DatosPartogramaPdf): void {
  if (!partogramaTieneDatos(datos)) return;

  let y = origen.yTop - 12;
  pagina.drawText('Partograma — dilatación cervical', { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO;

  const areaTop = y;
  const areaBottom = y - ALTO_GRAFICO;
  const areaX = origen.x + EJE_Y_W;
  const areaAncho = anchoDisponible - EJE_Y_W - MARGEN_DERECHO;
  const xDeHora = (hora: number): number => areaX + (Math.min(Math.max(hora, 0), HORA_MAX) / HORA_MAX) * areaAncho;
  const yDeFila = (fila: number): number => areaBottom + ((Math.min(Math.max(fila, DILATACION_MIN), FILA_MAX) - DILATACION_MIN) / (FILA_MAX - DILATACION_MIN)) * ALTO_GRAFICO;
  const yDeDilatacion = (cm: number): number => yDeFila(Math.min(Math.max(cm, DILATACION_MIN), DILATACION_MAX));
  const yDeFcf = (fcf: number): number => yDeFila(filaDeFcf(fcf));

  pagina.drawRectangle({ x: areaX, y: areaBottom, width: areaAncho, height: ALTO_GRAFICO, borderColor: BORDE_SUTIL, borderWidth: 0.5, color: undefined });
  for (let fila = DILATACION_MIN; fila <= FILA_MAX; fila++) {
    const yy = yDeFila(fila);
    pagina.drawLine({ start: { x: areaX, y: yy }, end: { x: areaX + areaAncho, y: yy }, thickness: 0.35, color: BORDE_SUTIL });
    // Eje izquierdo: dilatación en cm (1-10); eje derecho: FCF en lpm (100-200).
    if (fila <= DILATACION_MAX) pagina.drawText(String(fila), { x: origen.x, y: yy - 3, size: 6.5, font: fontRegular, color: MUTED });
    pagina.drawText(String(FCF_MIN + (fila - DILATACION_MIN) * 10), { x: areaX + areaAncho + 3, y: yy - 2.5, size: 5.5, font: fontRegular, color: MUTED });
  }
  for (let hora = 0; hora <= HORA_MAX; hora++) {
    const xx = xDeHora(hora);
    pagina.drawLine({ start: { x: xx, y: areaBottom }, end: { x: xx, y: areaTop }, thickness: 0.35, color: BORDE_SUTIL });
  }
  // Escala de planos de Hodge / De Lee del margen derecho.
  const xEscala = areaX + areaAncho + 24;
  for (const p of PLANOS_HODGE_PARTOGRAMA) {
    pagina.drawText(`${p.plano} ${p.deLee}`, { x: xEscala, y: yDeFcf(p.fcfEquivalente) - 2.5, size: 5.5, font: fontRegular, color: MUTED });
  }

  if (datos.paridad && datos.membranas) {
    const curva = curvaAlertaPartograma(datos.horaBaseLineaAlerta ?? 0, datos.paridad, datos.membranas);
    for (let i = 1; i < curva.length; i++) {
      pagina.drawLine({
        start: { x: xDeHora(curva[i - 1].hora), y: yDeDilatacion(curva[i - 1].dilatacion) },
        end: { x: xDeHora(curva[i].hora), y: yDeDilatacion(curva[i].dilatacion) },
        thickness: 1,
        color: CLAY,
        dashArray: [3, 2],
      });
    }
  }

  const puntosOrdenados = [...datos.puntos].sort((a, b) => a.hora - b.hora);
  for (const [i, punto] of puntosOrdenados.entries()) {
    const cx = xDeHora(punto.hora);
    const cy = yDeDilatacion(punto.dilatacion);
    pagina.drawCircle({ x: cx, y: cy, size: 2, color: PINE });
    if (i > 0) {
      const anterior = puntosOrdenados[i - 1];
      pagina.drawLine({ start: { x: xDeHora(anterior.hora), y: yDeDilatacion(anterior.dilatacion) }, end: { x: cx, y: cy }, thickness: 1.2, color: PINE });
    }
  }

  // FCF: cuadrito hueco por punto, unido por una línea fina.
  const fcfOrdenada = [...(datos.fcf ?? [])].sort((a, b) => a.hora - b.hora);
  for (const [i, p] of fcfOrdenada.entries()) {
    const cx = xDeHora(p.hora);
    const cy = yDeFcf(p.fcf);
    if (i > 0) pagina.drawLine({ start: { x: xDeHora(fcfOrdenada[i - 1].hora), y: yDeFcf(fcfOrdenada[i - 1].fcf) }, end: { x: cx, y: cy }, thickness: 0.6, color: MUTED });
    pagina.drawRectangle({ x: cx - 2, y: cy - 2, width: 4, height: 4, borderColor: INK, borderWidth: 0.8, color: undefined });
    // Fuera de la escala impresa (100-200) el cuadro queda pegado al borde: se rotula el valor real para no falsear el dato.
    if (p.fcf < FCF_MIN || p.fcf > FCF_MIN + (FILA_MAX - DILATACION_MIN) * 10) {
      pagina.drawText(String(p.fcf), { x: cx + 3, y: cy - 2, size: 5.5, font: fontRegular, color: CLAY });
    }
  }

  // Planos de Hodge y variedad de posición: círculo con trazo vertical + variedad al lado.
  for (const p of datos.hodge ?? []) {
    const plano = PLANOS_HODGE_PARTOGRAMA.find((h) => h.plano === p.plano);
    if (!plano) continue;
    const cx = xDeHora(p.hora);
    const cy = yDeFcf(plano.fcfEquivalente);
    pagina.drawCircle({ x: cx, y: cy, size: 2.5, borderColor: INK, borderWidth: 0.8, color: undefined });
    pagina.drawLine({ start: { x: cx, y: cy - 3.5 }, end: { x: cx, y: cy + 3.5 }, thickness: 0.6, color: INK });
    if (p.variedad) pagina.drawText(p.variedad, { x: cx + 3, y: cy + 3, size: 5.5, font: fontRegular, color: INK });
  }

  // Dips (I, II, V, M) junto a la FCF de esa hora, o sobre la línea de 100 lpm si no hay FCF. Varios dips en la misma hora se apilan.
  const dipsPorHora = new Map<number, number>();
  for (const d of datos.dips ?? []) {
    const indice = dipsPorHora.get(d.hora) ?? 0;
    dipsPorHora.set(d.hora, indice + 1);
    const fcfDeHora = fcfOrdenada.find((f) => f.hora === d.hora);
    const base = fcfDeHora ? yDeFcf(fcfDeHora.fcf) - 10 : yDeFila(DILATACION_MIN) + 3;
    const cy = Math.max(base - indice * 7, areaBottom + 2);
    pagina.drawText(d.tipo, { x: xDeHora(d.hora) + 3, y: cy, size: 7, font: fontRegular, color: CLAY });
  }

  // Ruptura de membranas: rótulo REM/RAM en la parte alta de la columna de esa hora.
  const rupturas: readonly (readonly [number | undefined, string, number])[] = [
    [datos.rem, 'REM', 8],
    [datos.ram, 'RAM', 15],
  ];
  for (const [hora, etiqueta, bajada] of rupturas) {
    if (hora === undefined) continue;
    const xx = xDeHora(hora);
    const anchoEtiqueta = fontRegular.widthOfTextAtSize(etiqueta, 6);
    // Al borde derecho el rótulo se pone a la izquierda del trazo para no pisar la escala FCF.
    const xEtiqueta = xx + 1.5 + anchoEtiqueta > areaX + areaAncho ? xx - 1.5 - anchoEtiqueta : xx + 1.5;
    pagina.drawLine({ start: { x: xx, y: areaTop - bajada - 1 }, end: { x: xx, y: areaTop }, thickness: 0.8, color: CLAY });
    pagina.drawText(etiqueta, { x: xEtiqueta, y: areaTop - bajada, size: 6, font: fontRegular, color: CLAY });
  }

  y = areaBottom - ALTO_EJE_X + 9;
  pagina.drawText('0h', { x: areaX, y, size: 7, font: fontRegular, color: MUTED });
  pagina.drawText('14h', { x: areaX + areaAncho - 16, y, size: 7, font: fontRegular, color: MUTED });
  y -= ALTO_EJE_X - 3;

  pagina.drawRectangle({ x: origen.x, y: y - 6, width: 8, height: 3, color: PINE });
  pagina.drawText('Dilatación registrada', { x: origen.x + 11, y: y - 5, size: 7, font: fontRegular, color: INK });
  if (datos.paridad && datos.membranas) {
    const x2 = origen.x + 11 + fontRegular.widthOfTextAtSize('Dilatación registrada', 7) + 14;
    pagina.drawRectangle({ x: x2, y: y - 6, width: 8, height: 3, color: CLAY });
    pagina.drawText(`Curva de alerta p10 (${datos.paridad}, membranas ${datos.membranas})`, { x: x2 + 11, y: y - 5, size: 7, font: fontRegular, color: INK });
  }
  y -= ALTO_LEYENDA;
  pagina.drawText('FCF: cuadro hueco (lpm, eje derecho). Hodge: círculo con trazo (I-IV / De Lee). Dips: I, II, V, M. REM/RAM: ruptura espontánea/artificial de membranas.', {
    x: origen.x,
    y: y - 5,
    size: 5.8,
    font: fontRegular,
    color: MUTED,
    maxWidth: anchoDisponible,
  });
  y -= ALTO_LEYENDA + 6;

  if (datos.filasHorarias.length > 0) {
    dibujarTablaHoraria(pagina, { x: origen.x, yTop: y }, anchoDisponible, fontRegular, datos.filasHorarias);
  }
}
